import { ListPlacesData, useListPlacesQuery } from '../../../queries';
import React, { useCallback, useEffect, useRef } from 'react';
import {
  VirtualizedList,
  useDebouncedState,
  useScrollThreshold,
} from '@mint/ui';
import { AdminPlacesTableProps } from './admin-places-table-props';
import { AdminPlacesTableRowActionsButton } from './admin-places-table-row-actions-button';
import { AdminTable } from '../admin-table';
import { AdminTableBody } from '../admin-table-body';
import { AdminTableColumn } from '../admin-table-column';
import { AdminTableColumnText } from '../admin-table-column-text';
import { AdminTableHead } from '../admin-table-head';
import { AdminTableRow } from '../admin-table-row';

const PAGE_SIZE = 30;

export const AdminPlacesTable: React.FunctionComponent<
  AdminPlacesTableProps
> = (): React.ReactElement => {
  const [query, setQuery] = useDebouncedState('', 400);

  const { data, error, fetchMore, loading } = useListPlacesQuery(
    { first: PAGE_SIZE, query: query.trim() },
    { fetchPolicy: 'cache-and-network' },
  );

  const scrollableRef = useRef<HTMLDivElement>(null);

  const bindScrollThreshold = useScrollThreshold(
    () => {
      if (!data || !data.places.pageInfo.hasNextPage) {
        return;
      }
      fetchMore({
        updateQuery: (prev: ListPlacesData, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            ...prev,
            places: {
              ...prev.places,
              edges: [...prev.places.edges, ...fetchMoreResult.places.edges],
              pageInfo: fetchMoreResult.places.pageInfo,
            },
          };
        },
        variables: {
          first: PAGE_SIZE,
          after: data ? data.places.pageInfo.endCursor : undefined,
        },
      });
    },
    {
      disabled: loading,
      ref: scrollableRef,
    },
  );

  const onQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
    },
    [],
  );

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({ top: 0 });
    }
  }, [query]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <input onChange={onQueryChange} />
      <AdminTable>
        <AdminTableHead>
          <AdminTableRow>
            <AdminTableColumn name="name">
              <AdminTableColumnText>Name</AdminTableColumnText>
            </AdminTableColumn>
          </AdminTableRow>
        </AdminTableHead>
        <VirtualizedList
          {...bindScrollThreshold}
          component={AdminTableBody}
          style={{ flex: 1 }}
          initialBatchSize={40}
          batchSize={2}
          ref={scrollableRef}
          threshold={200}
          overscan={200}
        >
          {!loading && !error && data
            ? data.places.edges.map(
                ({ node: { id, name } }): React.ReactElement => (
                  <AdminTableRow key={id}>
                    <AdminTableColumn name="title">
                      <AdminTableColumnText>
                        {name ? name : <i>Untitled</i>}
                      </AdminTableColumnText>
                      <AdminPlacesTableRowActionsButton
                        placeId={id}
                        placeName={name}
                      />
                    </AdminTableColumn>
                  </AdminTableRow>
                ),
              )
            : null}
          {/* <TableRow>
          <TableColumn>
            <Spinner />
          </TableColumn>
        </TableRow> */}
        </VirtualizedList>
      </AdminTable>
    </div>
  );
};
